<script setup lang="ts">
import { computed } from 'vue';
import { format, formatISO, parseISO, setHours, setMinutes, setSeconds } from 'date-fns';
import { formatDateTime } from 'vince-core/utils/formatting';
import { defaultDateFormat } from 'vince-core/constants/localization';

const props = defineProps({
    withTime: {
        type: Boolean,
        default: false
    },
    displayDateFormat: {
        type: String,
        default: undefined
    },
    combinedDateTime: {
        type: Boolean,
        default: undefined
    },
    timeFormat: {
        type: String,
        default: 'HH:mm:ss'
    },
    modelValue: {
        type: String,
        default: undefined
    },
    date: {
        type: String,
        default: undefined
    },
    time: {
        type: String,
        default: undefined
    }
});

const emit = defineEmits(['update:date', 'update:time', 'update:modelValue']);

const modelDate = computed({
    get: () => {
        if (!props.combinedDateTime) {
            if (!props.date) { return null; }
            let date = parseISO(formatDateTime(props.date, defaultDateFormat));
            if (!props.time) {
                return date;
            } else {
                const [hours, minutes, seconds] = props.time.split(':');
                date = setHours(date, parseInt(hours!));

                if (minutes) {
                    date = setMinutes(date, parseInt(minutes));
                }

                if (seconds) {
                    date = setSeconds(date, parseInt(seconds));
                }

                return date;
            }
        } else {
            return props.date ? parseISO(props.date) : null;
        }
    },
    set: (newDate: Date | null) => {
        let dateTime, date, time;
        if (!newDate) {
            dateTime = date = time = null;
        } else if (props.combinedDateTime) {
            dateTime = formatISO(newDate);
        } else {
            date = format(newDate, 'yyyy-MM-dd');
            time = format(newDate, props.timeFormat);
        }
        if (props.combinedDateTime) {
            emit('update:modelValue', dateTime);
        } else {
            emit('update:date', date);
            emit('update:time', time);
        }
    }
});
</script>

<template>
    <VLDatePicker
        v-model="modelDate"
        :show-time="withTime"
        hour-format="24"
        :date-format="displayDateFormat"
        v-bind="$attrs"
        show-icon
        icon-display="input"
    >
        <template v-if="modelDate" #inputicon>
            <i 
                class="pi pi-times"
                @click="modelDate = null"
            />
        </template>
    </VLDatePicker>
</template>
